import { Loader2, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { formatDateTime, formatDay } from "@/lib/format"
import type { KnowledgeDocumentDetail } from "../../domain/entities/document"
import { StatusBadge } from "./status-badge"
import { TypeBadge } from "./type-badge"
import { UesTab } from "./ues-tab"

const splitEntities = (raw: string): string[] =>
  raw
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)

const MetadataBadge = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
    {children}
  </span>
)

export type DocumentMetadataPanelProps = {
  doc: KnowledgeDocumentDetail
  kbName: string | undefined
  isExtracting: boolean
  isRedoing: boolean
  canRedoMetadata: boolean
  onRedo: () => void
  redoPending: boolean
}

// Metadata is read-only in the UI: manual editing was removed (the backend PUT
// /documents/{id}/metadata endpoint is gone) so the sole edit source of truth is
// re-extraction. `Re-extract` (redo) re-runs extraction end to end.
export const DocumentMetadataPanel = ({
  doc,
  kbName,
  isExtracting,
  isRedoing,
  canRedoMetadata,
  onRedo,
  redoPending,
}: DocumentMetadataPanelProps) => {
  const hasDocCode = Boolean(doc.metadata?.docCode)
  const hasValidity = Boolean(doc.metadata?.validFrom || doc.metadata?.validTo)

  return (
    <div className="space-y-5 text-sm">
      {/* Top section — always visible, above the tabs */}
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={doc.status} />
        <TypeBadge type={doc.documentType} />
      </div>

      <dl className="grid gap-2 sm:grid-cols-[auto_1fr] sm:gap-x-4">
        <dt className="text-muted-foreground">Knowledge base</dt>
        <dd className="min-w-0 break-words">{kbName ?? "—"}</dd>

        <dt className="text-muted-foreground">Created</dt>
        <dd>{formatDateTime(doc.createdAt)}</dd>

        <dt className="text-muted-foreground">Updated</dt>
        <dd>{formatDateTime(doc.updatedAt)}</dd>
      </dl>

      <Tabs defaultValue="core" className="space-y-5">
        <TabsList>
          <TabsTrigger value="core">Core metadata</TabsTrigger>
          <TabsTrigger value="ues">UES</TabsTrigger>
        </TabsList>

        <TabsContent value="core" className="space-y-4">
          {canRedoMetadata && (
            <div className="flex">
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={onRedo}
                disabled={redoPending}
              >
                {redoPending ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                Re-extract
              </Button>
            </div>
          )}

          {isExtracting || isRedoing ? (
            <div className="flex flex-col items-center gap-3 border-t py-10 text-muted-foreground">
              <Loader2 className="size-8 animate-spin text-primary" />
              <p>Extracting metadata…</p>
            </div>
          ) : doc.metadata ? (
            <div className="space-y-4">
              {(hasDocCode || hasValidity) && (
                <dl className="grid gap-2 sm:grid-cols-[auto_1fr] sm:items-center sm:gap-x-4">
                  {hasDocCode && (
                    <>
                      <dt className="text-muted-foreground">Doc code</dt>
                      <dd className="font-mono break-words">
                        {doc.metadata.docCode}
                      </dd>
                    </>
                  )}
                  {hasValidity && (
                    <>
                      <dt className="text-muted-foreground">Valid</dt>
                      <dd>
                        {formatDay(doc.metadata.validFrom)} →{" "}
                        {formatDay(doc.metadata.validTo)}
                      </dd>
                    </>
                  )}
                </dl>
              )}

              {doc.metadata.topics.length > 0 && (
                <div>
                  <p className="mb-2 text-muted-foreground">
                    Topics ({doc.metadata.topics.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {doc.metadata.topics.map((topic, index) => (
                      <MetadataBadge key={`${topic}-${index}`}>
                        {topic}
                      </MetadataBadge>
                    ))}
                  </div>
                </div>
              )}

              {doc.metadata.titles.length > 0 && (
                <div>
                  <p className="mb-2 text-muted-foreground">
                    Titles ({doc.metadata.titles.length})
                  </p>
                  <ul className="list-disc space-y-0.5 break-words pl-5">
                    {doc.metadata.titles.map((title, index) => (
                      <li key={`${title}-${index}`}>{title}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(() => {
                // Prefer the structured `entities` routing list; fall back to
                // the legacy semicolon `keyEntities` for docs not yet backfilled.
                const entities =
                  doc.metadata.entities.length > 0
                    ? doc.metadata.entities
                    : splitEntities(doc.metadata.keyEntities)
                return (
                  <div>
                    <p className="mb-2 text-muted-foreground">
                      Entities ({entities.length})
                    </p>
                    {entities.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {entities.map((entity, index) => (
                          <MetadataBadge key={`${entity}-${index}`}>
                            {entity}
                          </MetadataBadge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">—</p>
                    )}
                  </div>
                )
              })()}
            </div>
          ) : (
            <p className="text-muted-foreground">No metadata extracted yet.</p>
          )}
        </TabsContent>

        <TabsContent value="ues">
          <UesTab docId={doc.id} kbId={doc.kbId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
